// Studio API client.
//
// Every fetch goes through here so we get one place to:
//   - apply credentials: include (the session cookie rides on every call)
//   - parse JSON envelopes
//   - turn HTTP errors into typed StudioApiError instances
//   - track request timing for the dev console
//
// The base URL is supplied at runtime by the Liquid mount script
// (window.__MD_STUDIO_CONFIG__.apiBase) or falls back to the env var
// VITE_STUDIO_API_BASE for local development.

import type {
  ActivityListResponse,
  BrandKit,
  BrandKitPatch,
  CartAddDescriptor,
  CustomerSummary,
  Design,
  DesignsListQuery,
  DesignsListResponse,
  Folder,
  ForkResponse,
  PhotosListResponse,
  SavedPhoto,
} from './types';

declare global {
  interface Window {
    __MD_STUDIO_CONFIG__?: {
      apiBase: string;
      bridge?: {
        customerId: string;
        email: string;
        shopDomain: string;
        ts: number;
        sig: string;
      } | null;
      shopDomain?: string;
      customer?: {
        id: string;
        email: string;
        first_name?: string | null;
        last_name?: string | null;
      };
    };
  }
}

export class StudioApiError extends Error {
  status: number;
  code: string | null;
  details: unknown;
  constructor(message: string, status: number, code: string | null, details?: unknown) {
    super(message);
    this.name = 'StudioApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getApiBase(): string {
  if (typeof window !== 'undefined' && window.__MD_STUDIO_CONFIG__?.apiBase) {
    return window.__MD_STUDIO_CONFIG__.apiBase.replace(/\/$/, '');
  }
  const fromEnv = import.meta.env.VITE_STUDIO_API_BASE as string | undefined;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return 'https://md-unified-inbox-backend.fly.dev';
}

async function request<T>(
  path: string,
  init?: RequestInit & { searchParams?: Record<string, string | number | boolean | undefined | null> },
): Promise<T> {
  const base = getApiBase();
  const url = new URL(`${base}${path}`);
  if (init?.searchParams) {
    for (const [k, v] of Object.entries(init.searchParams)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const resp = await fetch(url.toString(), {
    ...init,
    headers,
    credentials: 'include',
  });
  if (resp.status === 204) {
    return undefined as unknown as T;
  }
  let body: unknown;
  const text = await resp.text();
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = text;
  }
  if (!resp.ok) {
    const err = body as { error?: string; message?: string; details?: unknown } | string | undefined;
    const code = typeof err === 'object' && err && 'error' in err ? (err.error ?? null) : null;
    const message =
      (typeof err === 'object' && err && 'message' in err && err.message) ||
      (typeof err === 'object' && err && 'error' in err && err.error) ||
      `Request failed with status ${resp.status}`;
    throw new StudioApiError(String(message), resp.status, code, body);
  }
  return body as T;
}

// ─── Auth ────────────────────────────────────────────────────────────────

export async function bridgeAuth(payload: {
  customerId: string;
  email: string;
  shopDomain: string;
  ts: number;
  sig: string;
}): Promise<{ ok: true; email: string; shopDomain: string }> {
  return request('/storefront/studio/auth/bridge', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function requestOtp(email: string): Promise<{ ok: true; expires_at: string }> {
  return request('/storefront/studio/auth/request-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyOtp(args: { email: string; code: string; shopDomain: string }): Promise<{ ok: true; email: string; shopDomain: string }> {
  return request('/storefront/studio/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export interface StaffActingAs {
  email: string;
  role: 'owner' | 'staff';
}

export async function fetchMe(): Promise<{
  customer: CustomerSummary;
  staff_acting_as: StaffActingAs | null;
  session_expires_at: string;
}> {
  return request('/storefront/studio/auth/me');
}

/**
 * Swap a one-shot staff_token (URL query param) for a staff session cookie.
 * Called by the SPA on boot when ?staff_token= is present.
 */
export async function staffBridge(staff_token: string): Promise<{
  ok: true;
  customer: { id: string; email: string; shop_domain: string };
  expires_in: number;
}> {
  return request('/storefront/studio/auth/staff-bridge', {
    method: 'POST',
    body: JSON.stringify({ staff_token }),
  });
}

/**
 * Exit staff impersonation — clears the staff cookie. The SPA reloads
 * after this so the regular customer cookie (if any) becomes active again.
 */
export async function exitStaffMode(): Promise<{ ok: true }> {
  return request('/storefront/portal-admin/impersonate/exit', { method: 'POST' });
}

// ─── Agents (My Team) ───────────────────────────────────────────────────

export interface Agent {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  website: string | null;
  license_number: string | null;
  brokerage: string | null;
  notes: string | null;
  is_self: boolean;
  display_order: number | null;
  headshot_storage_path: string | null;
  headshot_url: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export async function listAgents(params: { archived?: boolean } = {}): Promise<{ agents: Agent[] }> {
  const qs = params.archived ? '?archived=true' : '';
  return request(`/storefront/studio/agents${qs}`);
}

export async function createAgent(body: Partial<Agent>): Promise<{ agent: Agent }> {
  return request('/storefront/studio/agents', { method: 'POST', body: JSON.stringify(body) });
}

export async function patchAgent(id: string, body: Partial<Agent>): Promise<{ agent: Agent }> {
  return request(`/storefront/studio/agents/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function archiveAgent(id: string): Promise<{ agent: Agent }> {
  return request(`/storefront/studio/agents/${id}/archive`, { method: 'POST' });
}

export async function restoreAgent(id: string): Promise<{ agent: Agent }> {
  return request(`/storefront/studio/agents/${id}/restore`, { method: 'POST' });
}

export async function uploadAgentHeadshot(
  id: string,
  file: File,
): Promise<{ agent: Agent }> {
  // Read file → base64. Sized check happens server-side too.
  const reader = new FileReader();
  const base64 = await new Promise<string>((resolve, reject) => {
    reader.onload = () => {
      const result = reader.result as string;
      // result is a data URL: "data:image/jpeg;base64,...". Strip prefix.
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  return request(`/storefront/studio/agents/${id}/headshot`, {
    method: 'POST',
    body: JSON.stringify({ file_base64: base64, content_type: file.type }),
  });
}

export async function clearAgentHeadshot(id: string): Promise<{ agent: Agent }> {
  return request(`/storefront/studio/agents/${id}/headshot`, { method: 'DELETE' });
}

// ─── Design ↔ Agent assignment (Ship 3) ─────────────────────────────────

export async function listDesignAgents(designId: string): Promise<{ agents: Agent[] }> {
  return request(`/storefront/studio/designs/${designId}/agents`);
}

export async function assignAgentsToDesign(
  designId: string,
  agentIds: string[],
): Promise<{ ok: true; assigned: number }> {
  return request(`/storefront/studio/designs/${designId}/agents`, {
    method: 'POST',
    body: JSON.stringify({ agent_ids: agentIds }),
  });
}

export async function unassignAgentFromDesign(
  designId: string,
  agentId: string,
): Promise<{ ok: true }> {
  return request(`/storefront/studio/designs/${designId}/agents/${agentId}`, {
    method: 'DELETE',
  });
}

export async function listAgentDesigns(
  agentId: string,
  params: { archived?: boolean } = {},
): Promise<{ designs: unknown[] }> {
  const qs = params.archived ? '?archived=true' : '';
  return request(`/storefront/studio/agents/${agentId}/designs${qs}`);
}

export async function assignDesignsToAgent(
  agentId: string,
  designIds: string[],
): Promise<{ ok: true; assigned: number }> {
  return request(`/storefront/studio/agents/${agentId}/designs`, {
    method: 'POST',
    body: JSON.stringify({ design_ids: designIds }),
  });
}

export async function logout(): Promise<{ ok: true }> {
  return request('/storefront/studio/auth/logout', { method: 'POST' });
}

// ─── Designs ─────────────────────────────────────────────────────────────

export async function listDesigns(query: DesignsListQuery = {}): Promise<DesignsListResponse> {
  return request('/storefront/studio/designs', {
    searchParams: {
      folder: query.folder,
      status: query.status,
      product_type: query.product_type,
      source_kind: query.source_kind,
      search: query.search,
      sort: query.sort,
      limit: query.limit,
      cursor: query.cursor,
      archived: query.archived,
      pinned_first: query.pinned_first,
      agent_id: query.agent_id,
    },
  });
}

/** Mark a single design as viewed by its owner. Idempotent — only sets
 *  viewed_at if it was null. */
export async function markDesignViewed(id: string): Promise<{ ok: true }> {
  return request(`/storefront/studio/designs/${id}/view`, { method: 'POST' });
}

/** Bulk-mark every unviewed attached design as viewed. Used when the
 *  customer dismisses the new-design banner. */
export async function markAttachedViewed(): Promise<{ ok: true; marked: number }> {
  return request('/storefront/studio/designs/mark-attached-viewed', { method: 'POST' });
}

export async function getDesign(id: string): Promise<import('./types').DesignDetailResponse> {
  // Returns { design, family: { root, variants } } as of Ship 5c. Older
  // server responses without `family` still parse under the optional field
  // declaration in DesignDetailResponse — UI checks family before reading it.
  return request(`/storefront/studio/designs/${id}`);
}

export async function getDesignVariantOptions(id: string): Promise<import('./types').VariantOptionsResponse> {
  return request(`/storefront/studio/designs/${id}/variant-options`);
}

export async function patchDesign(
  id: string,
  body: {
    custom_name?: string | null;
    is_pinned?: boolean;
    folder_id?: string | null;
    archived?: boolean;
  },
): Promise<{ design: Design }> {
  return request(`/storefront/studio/designs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteDesign(id: string): Promise<void> {
  await request(`/storefront/studio/designs/${id}`, { method: 'DELETE' });
}

export async function restoreDesign(id: string): Promise<{ design: Design }> {
  return request(`/storefront/studio/designs/${id}/restore`, { method: 'POST' });
}

export async function forkDesign(
  id: string,
  body?: {
    custom_name?: string;
    prefill_brand_kit?: boolean;
    /** Ship 4: when set, the cloned DH project gets the agent's data
     *  substituted into {{agent_*}} placeholders, and the new design
     *  is auto-assigned to the agent. */
    agent_id?: string;
  },
): Promise<ForkResponse> {
  return request(`/storefront/studio/designs/${id}/fork`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

export async function reorderDesign(
  id: string,
  body?: { quantity?: number; variant_id?: number },
): Promise<CartAddDescriptor> {
  return request(`/storefront/studio/designs/${id}/reorder`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  });
}

export async function bulkReorder(items: Array<{ design_id: string; quantity: number; variant_id?: number }>): Promise<CartAddDescriptor> {
  return request('/storefront/studio/designs/bulk/reorder', {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

export async function bulkArchive(ids: string[]): Promise<{ archived: number }> {
  return request('/storefront/studio/designs/bulk/archive', {
    method: 'POST',
    body: JSON.stringify({ design_ids: ids }),
  });
}

export async function bulkMove(ids: string[], folderId: string | null): Promise<{ moved: number }> {
  return request('/storefront/studio/designs/bulk/move', {
    method: 'POST',
    body: JSON.stringify({ design_ids: ids, folder_id: folderId }),
  });
}

export async function bulkPin(ids: string[], pinned: boolean): Promise<{ updated: number }> {
  return request('/storefront/studio/designs/bulk/pin', {
    method: 'POST',
    body: JSON.stringify({ design_ids: ids, pinned }),
  });
}

// ─── Brand Kit ───────────────────────────────────────────────────────────

export async function getBrandKit(): Promise<{ brand_kit: BrandKit }> {
  return request('/storefront/studio/brand-kit');
}

export async function patchBrandKit(body: BrandKitPatch): Promise<{ brand_kit: BrandKit }> {
  return request('/storefront/studio/brand-kit', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Upload a logo or headshot. Reads the File into base64 client-side and
 * sends it as JSON. Easiest cross-runtime path on Cloudflare Pages /
 * Fastify; multipart would need extra middleware.
 */
export async function uploadBrandAsset(
  kind: 'logo' | 'headshot',
  file: File,
): Promise<{ brand_kit: BrandKit }> {
  const data = await fileToBase64(file);
  return request('/storefront/studio/brand-kit/upload', {
    method: 'POST',
    body: JSON.stringify({
      kind,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      data,
    }),
  });
}

export async function deleteBrandAsset(kind: 'logo' | 'headshot'): Promise<{ brand_kit: BrandKit }> {
  return request('/storefront/studio/brand-kit/upload', {
    method: 'DELETE',
    body: JSON.stringify({ kind }),
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string'));
        return;
      }
      // strip the "data:<mime>;base64," prefix
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.substring(idx + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

// ─── Photo library ───────────────────────────────────────────────────────

export async function listPhotos(args?: {
  tag?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}): Promise<PhotosListResponse> {
  return request('/storefront/studio/photos', {
    searchParams: {
      tag: args?.tag,
      search: args?.search,
      limit: args?.limit,
      cursor: args?.cursor,
    },
  });
}

export async function uploadPhoto(file: File, tags?: string[]): Promise<{ photo: SavedPhoto }> {
  const data = await fileToBase64(file);
  return request('/storefront/studio/photos/upload', {
    method: 'POST',
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      data,
      tags: tags ?? [],
    }),
  });
}

export async function patchPhoto(
  id: string,
  body: { filename?: string; tags?: string[]; bump_last_used?: boolean },
): Promise<{ photo: SavedPhoto }> {
  return request(`/storefront/studio/photos/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deletePhoto(id: string): Promise<void> {
  await request(`/storefront/studio/photos/${id}`, { method: 'DELETE' });
}

// ─── Activity ────────────────────────────────────────────────────────────

export async function listActivity(args?: {
  limit?: number;
  cursor?: string;
  designId?: string;
}): Promise<ActivityListResponse> {
  return request('/storefront/studio/activity', {
    searchParams: {
      limit: args?.limit,
      cursor: args?.cursor,
      design_id: args?.designId,
    },
  });
}

// ─── Folders ─────────────────────────────────────────────────────────────

export async function listFolders(): Promise<{ folders: Folder[] }> {
  return request('/storefront/studio/folders');
}

export async function createFolder(body: {
  name: string;
  icon?: string;
  color_tint?: string;
}): Promise<{ folder: Folder }> {
  return request('/storefront/studio/folders', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function patchFolder(
  id: string,
  body: {
    name?: string;
    icon?: string | null;
    color_tint?: string | null;
    sort_order?: number;
  },
): Promise<{ folder: Folder }> {
  return request(`/storefront/studio/folders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteFolder(id: string, moveDesignsTo?: string): Promise<void> {
  await request(`/storefront/studio/folders/${id}`, {
    method: 'DELETE',
    searchParams: moveDesignsTo ? { move_designs_to: moveDesignsTo } : undefined,
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/**
 * Dispatch a cart-add descriptor (returned from reorder endpoints) to the
 * storefront's /cart/add.js. Same-origin call so credentials flow naturally.
 *
 * Resolves to the cart URL the caller should navigate to on success.
 */
export async function dispatchCartAdd(descriptor: CartAddDescriptor): Promise<string> {
  const resp = await fetch(descriptor.cart_add_endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: descriptor.items }),
  });
  if (!resp.ok) {
    let detail = '';
    try {
      detail = (await resp.json()).description ?? '';
    } catch {
      /* noop */
    }
    throw new StudioApiError(detail || 'Could not add to cart', resp.status, null);
  }
  return `https://${descriptor.shop_domain}/cart`;
}
